import { useEffect, useMemo, useState } from "react";
import { fetchShareSnapshot } from "../api/client";
import { normalizeProjectPayload } from "../App";
import type { FilmProject } from "../types";
import { assetDisplayUrl, matchPeople, matchPlaces } from "../utils/assetMatching";
import { SimpleCut } from "./SimpleMode";

/**
 * The share-link viewer: what a producer, financier or collaborator sees when a director
 * sends them a Kinetix link. The full Watch experience — cut, captions, voice reads,
 * pitch page, even saving the animatic as a video — with every editing and rendering
 * affordance removed. No script text travels with the snapshot.
 */
export function ShareView({ token }: { token: string }) {
  const [project, setProject] = useState<FilmProject | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "missing" | "offline">("loading");

  useEffect(() => {
    let cancelled = false;
    fetchShareSnapshot(token)
      .then((response) => {
        if (cancelled) return;
        if (response.ok && response.project) {
          setProject(normalizeProjectPayload(response.project as Partial<FilmProject>));
          setStatus("ready");
        } else {
          setStatus("missing");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("offline");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const people = useMemo(
    () => (project ? matchPeople(project.characters, project.uploadedAssets) : []),
    [project]
  );
  const places = useMemo(
    () => (project ? matchPlaces(project.scenes, project.uploadedAssets) : []),
    [project]
  );

  const noopAsync = async () => false;

  return (
    <div className="simple-mode share-view">
      <nav className="simple-nav">
        <span className="simple-brand">
          <b>K</b>
          Kinetix Film
        </span>
        <span className="simple-nav-actions">
          <a className="simple-nav-new share-cta" href={`${window.location.origin}${window.location.pathname}`}>
            Make your own film
          </a>
        </span>
      </nav>
      {status === "loading" ? (
        <main className="simple-screen share-empty">
          <h1>Opening the screening room…</h1>
        </main>
      ) : null}
      {status === "missing" ? (
        <main className="simple-screen share-empty">
          <h1>This link has expired</h1>
          <p className="simple-sub">The film isn't here anymore — ask whoever sent it for a fresh link.</p>
        </main>
      ) : null}
      {status === "offline" ? (
        <main className="simple-screen share-empty">
          <h1>Can't reach the screening room</h1>
          <p className="simple-sub">Check your connection and reload this page.</p>
        </main>
      ) : null}
      {status === "ready" && project ? (
        <header className="share-hero">
          {project.posterAsset ? (
            <span className="share-hero-poster">
              <img alt={`${project.title} poster`} src={assetDisplayUrl(project.posterAsset, 480) || ""} />
            </span>
          ) : null}
          <div className="share-hero-copy">
            <h1>{project.title}</h1>
            {(() => {
              const pitch = (project.pitchKit || {}) as Record<string, unknown>;
              const coverage = (project.coverage || {}) as Record<string, unknown>;
              const loglines = Array.isArray(pitch.loglines) ? pitch.loglines : [];
              const logline = (typeof loglines[0] === "string" && loglines[0]) || (typeof coverage.logline === "string" && coverage.logline) || project.goal || "";
              const taglines = Array.isArray(pitch.taglines) ? pitch.taglines : [];
              const tagline = typeof taglines[0] === "string" ? taglines[0] : "";
              return (
                <>
                  {tagline ? <p className="share-hero-tag">“{tagline}”</p> : null}
                  {logline ? <p className="share-hero-logline">{logline}</p> : null}
                  <p className="share-hero-meta">
                    {project.genre ? `${project.genre} · ` : ""}
                    {project.scenes.length} scenes · {Math.round(project.scenes.reduce((sum, scene) => sum + scene.durationSeconds, 0))}s
                    {" · made with Kinetix Film"}
                  </p>
                </>
              );
            })()}
          </div>
        </header>
      ) : null}
      {status === "ready" && project ? (
        <SimpleCut
          project={project}
          people={people}
          places={places}
          readOnly
          onRemakeScene={() => undefined}
          onRestoreTake={() => undefined}
          onSaveSceneNote={() => undefined}
          onShareLink={() => undefined}
          isSharingLink={false}
          onMakePoster={() => undefined}
          isMakingPoster={false}
          onSendToRender={noopAsync}
          isSavingPackage={false}
          isEasyModeRunning={false}
        />
      ) : null}
      {status === "ready" ? (
        <footer className="share-footer">
          Shared as a previsualization — nothing here is final footage. Made with Kinetix Film.
        </footer>
      ) : null}
    </div>
  );
}
